import { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";

// Components
import { Typography, Box } from "@mui/material";
import { SquaredButton } from "pages/ProjectDashboard";
import MessageUpgrade from "components/Pro/Messages/MessageUpgrade/index";
import { CreateProjectSectionModal, useCreateProjectSectionModal } from "components/CreateProjectSectionModal";
import Loader from "components/Loader";

// Hooks
import { useAbility } from "@casl/react";
import useResizeObserver from "use-resize-observer";
import { useAppSelector, useAppDispatch } from "app/hooks";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useGraphsFetch from "utils/hooks/useGraphsFetch";
import { shallowEqual } from "react-redux";

// Ability
import { AbilityContext, compoundPermissions } from "context/Ability";

// Store
import { toggleLoader } from "store/slice/UI";
import { fetchCharts, getCharts, getLD9 } from "store/slice/charts";
import { fetchPersonSet, getPeopleSets, getPersonSetByPath } from "store/slice/Project/peopleSet/peopleSetSlice";
import {
    fetchCompanySet,
    getCompanySets,
    getCompanySetByPath,
} from "store/slice/Project/companiesSet/companiesSetSlice";
import { getInsightById, getProjectInsight } from "store/slice/Project/insights/insightsSlice";
import { getBriefs } from "store/slice/Project/briefs/briefsSlice";

// Images
import createBrief from "assets/images/svgs/create-brief-white.svg";

// Types
import { BriefChildren } from "services/projects/project.types";

// Styles
import PathToImprovementStyles from "./PathToImprovement.styles";

// Others
import ldCharts from "../chartsListConfig";
import { urlPaths } from "enums/urlPaths";
import { ChartIds } from "enums/chartEnums";
import { getInsightPeersPath, getInsightRelatedSltPath } from "pages/ProjectDashboard/adapters/formattedData";

import icons from "enums/icons";
import { isEmpty } from "lodash";

const PathToImprovement = (): JSX.Element => {
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const ability = useAbility(AbilityContext);

    const { companyId } = useParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");

    const { status: chartsStatus } = useAppSelector(getCharts);
    const { status: sltSetStatus } = useAppSelector(getPeopleSets);
    const { status: peersSetStatus } = useAppSelector(getCompanySets);
    const { data: briefsData } = useAppSelector(getBriefs);
    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);
    const bridgeChartData = useAppSelector(getLD9);
    const [bridgeChart] = ldCharts.filter((chart) => chart.id === ChartIds.Bridge);
    const { ref: chartWrapper, width = 0, height = 0 } = useResizeObserver<HTMLDivElement>();

    const graphData = useGraphsFetch([ChartIds.Bridge]);
    const relatedPeersPath = getInsightPeersPath(insightData)?.ref?.path;
    const relatedSltPath = getInsightRelatedSltPath(insightData)?.ref?.path;
    const peerIds = useAppSelector(getCompanySetByPath(relatedPeersPath), shallowEqual)?.data?.company_ids;
    const sltIds = useAppSelector(getPersonSetByPath(relatedSltPath), shallowEqual)?.data?.person_ids;
    const canSeeBridge = bridgeChart ? compoundPermissions(bridgeChart.permissions, ability) : false;
    const classes = PathToImprovementStyles({ canSeeBridge });

    const hasBridgeChartData = Boolean(bridgeChartData?.series?.data);
    const selectedBoard = graphData.target_company_board;
    const hasDataForRequest = Boolean(
        projectId &&
            insightId &&
            insightData &&
            graphData.target_company_id &&
            sltIds !== undefined &&
            peerIds !== undefined &&
            selectedBoard.length
    );

    useEffect(() => {
        if (!projectId || !insightId || insightData) {
            return;
        }

        dispatch(
            getProjectInsight({
                projectId,
                path: `/project_${projectId}/insight_list/insight_${insightId}`,
            })
        );
    }, [dispatch, insightData, insightId, projectId]);

    useEffect(() => {
        if (!projectId || !insightData) {
            return;
        }

        if (relatedSltPath && sltSetStatus !== "fetching" && sltIds === undefined) {
            dispatch(fetchPersonSet({ projectId, path: relatedSltPath }));
        }

        if (relatedPeersPath && peersSetStatus !== "fetching" && peerIds === undefined) {
            dispatch(fetchCompanySet({ projectId, path: relatedPeersPath }));
        }
    }, [
        dispatch,
        insightData,
        peerIds,
        peersSetStatus,
        projectId,
        relatedPeersPath,
        relatedSltPath,
        sltIds,
        sltSetStatus,
    ]);

    useEffect(() => {
        if (!hasDataForRequest || chartsStatus !== "pristine" || !isEmpty(bridgeChartData) || !canSeeBridge) {
            return;
        }

        dispatch(
            fetchCharts({
                graphData,
                projectId: projectId as string,
                insightId: insightId as string,
                canSeeTenureChart: false,
            })
        );
    }, [bridgeChartData, canSeeBridge, chartsStatus, dispatch, graphData, hasDataForRequest, insightId, projectId]);

    const handleCallBackAfterSaving = async (createdSectionData: BriefChildren) => {
        const briefId = createdSectionData?.data.id;

        if (briefId) {
            dispatch(toggleLoader(false));
            navigate(`${urlPaths.Brief}/${companyId}?projectId=${projectId}&insightId=${insightId}&briefId=${briefId}`);
        }
    };

    const {
        showModalFor,
        newSectioName,
        handleShowCreateProjectSectionModal,
        handleCreateProjectSection,
        handleUpdateSectionName,
        handleCloseProjectSectionCreateModal,
    } = useCreateProjectSectionModal(projectId, insightId, handleCallBackAfterSaving);

    const isBriefNameInvalid = useMemo(
        () => briefsData.some((brief) => brief.data.name === newSectioName),
        [briefsData, newSectioName]
    );

    return (
        <Box className={classes.wrapper}>
            <Box className={classes.headerContainer}>
                <Typography className={classes.mainTitle} component="h1">
                    {intl.formatMessage({
                        id: "projection.pathToImprovement",
                    })}
                </Typography>
                <Typography paragraph className={classes.intro}>
                    {intl.formatMessage({ id: "projection.pathToImprovement.description" })}
                </Typography>
                <Typography className={classes.secondaryTitle} component="h2">
                    {intl.formatMessage({
                        id: "projection.leadershipBridge",
                    })}
                </Typography>
            </Box>

            <Box>
                {canSeeBridge ? (
                    <>
                        <Box ref={chartWrapper} className={classes.chartWrapper}>
                            {hasBridgeChartData ? (
                                bridgeChart?.graphComponent(width, height, {
                                    createBriefCallback: handleShowCreateProjectSectionModal,
                                })
                            ) : (
                                <Loader loading />
                            )}
                        </Box>
                        <Box className={classes.outroWrapper}>
                            <Box>
                                <Typography component="h3" className={classes.outroTitle}>
                                    Creating a Search Brief
                                </Typography>
                                <Typography paragraph className={classes.outroText}>
                                    {intl.formatMessage({ id: "projection.pathToImprovement.createBrief.description" })}
                                </Typography>
                            </Box>
                            <Box pl={2}>
                                <SquaredButton
                                    width={120}
                                    height={100}
                                    color="secondary"
                                    onClick={() => handleShowCreateProjectSectionModal("Brief")}
                                >
                                    <Typography sx={{ fontSize: 12, letterSpacing: 0.4, mb: 1 }}>
                                        Create Brief
                                    </Typography>
                                    <img src={createBrief} width={32} alt="" />
                                </SquaredButton>
                            </Box>
                        </Box>
                    </>
                ) : (
                    <Box className={classes.proGraphMsgWrapper}>
                        <MessageUpgrade width={300} title="pro.message.heading" icon={icons.proStar}>
                            <Typography>
                                {intl.formatMessage({ id: "leadershipDashboard.pathToImprovement.goPro.message" })}
                            </Typography>
                        </MessageUpgrade>
                    </Box>
                )}
            </Box>

            <CreateProjectSectionModal
                modalFor={showModalFor}
                isNameInvalid={isBriefNameInvalid}
                textInput={newSectioName}
                textInputPlaceholder=""
                handleTextInputChange={handleUpdateSectionName}
                handleCreateSection={handleCreateProjectSection}
                handleCloseModal={handleCloseProjectSectionCreateModal}
            />
        </Box>
    );
};

export default PathToImprovement;
